import { useCallback, useRef, useState } from "react";
import { getProducerCodeCounts } from "../../../../actions/CarrierAgreementMgmt/ProducerCodeConfig";

export type CountsDto = {
  InactiveCDRC: number;
  MappedCDRC: number;
  NotMappedCDRC: number;
  InactiveKeeper: number;
  MappedKeeper: number;
  NotMappedKeeper: number;
  InactiveAL3: number;
  MappedAL3: number;
  NotMappedAL3: number;
  TotalRows: number;
  ActiveCount: number;
  InactiveCount: number;
  InProductionCarrierCount: number;
  NotInProductionCarrierCount: number;
  MappedCDRCPct: number;
  MappedKeeperPct: number;
  MappedAL3Pct: number;
};

export type SearchDto = {
  FranchiseTypeId?: number | null;
  CarrierId?: number | null;
  OfficeCode?: string | null;
  OfficeCodes?: string[] | null;
  AcquisitionName?: string | null;
  BrandName?: string[] | null;
  StateCodes?: string[] | null;
  IsFranchise?: boolean | null;
  InProduction?: number | null;
};

export function useSearchProducerCodeDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [counts, setCounts] = useState<CountsDto | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
  }, []);

  const search = useCallback(async (payload?: SearchDto | null) => {
    const filterPayload: SearchDto | null =
      payload === undefined ? null : payload;

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await getProducerCodeCounts(filterPayload);

      const rawCounts = result?.counts ?? result ?? null;
      setCounts(rawCounts as CountsDto | null);

      setLoading(false);
      return { counts: rawCounts as CountsDto | null };
    } catch (err: any) {
      if (err && err.name === "AbortError") {
        setLoading(false);
        throw err;
      }
      setError(err ?? new Error("Unknown error"));
      setLoading(false);
      throw err;
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  return {
    loading,
    error,
    counts,
    search,
    cancel,
  };
}
